import { Bot, ChevronDown, Link2, Phone, Plus, Rocket } from 'lucide-react';
import { useLocation } from 'react-router-dom';

import ThemeToggle from '@/components/ThemeToggle';
import { Button } from '@/components/ui/button';

import { emit } from '@/hooks/use-event-handler';

import { useAgentDetailQuery } from '@/query/agent.query';

import { Permissions } from '@/constants';

import { ButtonGroup, ButtonGroupSeparator } from './ui/button-group';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { ProtectedComponent } from './ProtectedComponent';

const PublishAgentButton = () => {
  const { pathname } = useLocation();
  const agentId = pathname.startsWith('/agents/') ? pathname.split('/')[2] : undefined;

  const { data: agentDetail } = useAgentDetailQuery(agentId || '', {
    enabled: false,
  });

  if (agentDetail?.status === 'Published') {
    return null;
  }

  return (
    <Button onClick={() => emit('agents:publish')} disabled={!agentDetail?.can_publish}>
      <Rocket className="h-4 w-4" />
      Publish
    </Button>
  );
};

const TestAgentButtons = () => {
  const { pathname } = useLocation();
  const agentId = pathname.startsWith('/agents/') ? pathname.split('/')[2] : undefined;

  const { data: agentDetail } = useAgentDetailQuery(agentId || '', {
    enabled: false,
  });

  return (
    <>
      <ButtonGroup>
        <Button onClick={() => {}}>
          <Bot className="h-4 w-4" />
          Test Agent
        </Button>
        <ButtonGroupSeparator />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button className="!pl-2">
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => emit('agent:test:web')}>
              <Link2 className="h-4 w-4" />
              Web
            </DropdownMenuItem>
            {agentDetail?.has_telephony_configured && (
              <DropdownMenuItem onClick={() => emit('agent:test:phone')}>
                <Phone className="h-4 w-4" />
                Phone
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </ButtonGroup>

      <ProtectedComponent requiredPermissions={[Permissions.AgentPublish]}>
        <PublishAgentButton />
      </ProtectedComponent>
    </>
  );
};

export const PageActions = () => {
  const { pathname } = useLocation();

  return (
    <div className="ml-auto flex items-center gap-2">
      {/* Page actions */}
      <ProtectedComponent requiredPermissions={[Permissions.AgentCreateUpdate]}>
        {pathname === '/agents' && (
          <Button onClick={() => emit('agents:create')}>
            <Plus className="h-4 w-4" />
            Create an agent
          </Button>
        )}
      </ProtectedComponent>

      <ProtectedComponent requiredPermissions={[Permissions.PhoneNumberCreateUpdate]}>
        {pathname === '/phone-numbers' && (
          <Button onClick={() => emit('phone-number:create')}>
            <Plus className="h-4 w-4" />
            Add phone number
          </Button>
        )}
      </ProtectedComponent>

      <ProtectedComponent requiredPermissions={[Permissions.BatchCallCreateUpdate]}>
        {pathname === '/batch-calls' && (
          <Button onClick={() => emit('batch-calls:create')}>
            <Plus className="h-4 w-4" />
            Create batch call
          </Button>
        )}
      </ProtectedComponent>

      <ProtectedComponent requiredPermissions={[Permissions.ApiKeyCreateUpdate]}>
        {pathname === '/keys' && (
          <Button onClick={() => emit('key:create')}>
            <Plus className="h-4 w-4" />
            Add Key
          </Button>
        )}
      </ProtectedComponent>

      <ProtectedComponent requiredPermissions={[Permissions.KnowledgeBaseCreateUpdate]}>
        {pathname === '/knowledge-bases' && (
          <Button onClick={() => emit('knowledge-base:create')}>
            <Plus className="h-4 w-4" />
            Add Knowledge Base
          </Button>
        )}
      </ProtectedComponent>

      <ProtectedComponent requiredPermissions={[Permissions.ToolsCreateUpdate]}>
        {pathname === '/tools' && (
          <Button onClick={() => emit('tool:create')}>
            <Plus className="h-4 w-4" />
            Add Tool
          </Button>
        )}
      </ProtectedComponent>

      {pathname.startsWith('/agents/') && <TestAgentButtons />}
      {/** HeaderActions uses useLocation so it's safe to render here */}
      <ThemeToggle />
    </div>
  );
};
